/* Basemap options.
 *
 * The Lands Department tiles matter more here than they would on a generic map.
 * They are the Government's own cartography, they carry 繁體中文 labels, and the
 * aerial imagery shows reclamation fill directly — you can see the seawall and
 * the made ground. For a project arguing about what used to be water, official
 * provenance is part of the argument.
 *
 * Verified 2026-07: no API key, no registration, no Referer check, and
 * `Access-Control-Allow-Origin: *`. Free for commercial and non-commercial use
 * under the data.gov.hk terms, PROVIDED the attribution below is displayed.
 * The service publishes no numeric rate limit, only "shall not invoke the API
 * with large amount of requests within a short period".
 *
 * Two gotchas encoded below, both found the hard way:
 *   · the basemap raster starts at z10, but the label overlay starts at z9;
 *     declaring the wrong minzoom makes MapLibre request 204s forever.
 *   · there is no @2x endpoint, so labels are soft on retina displays. That is
 *     the price of baked-in Chinese labels; OpenFreeMap is the crisp option.
 */

const LANDSD_ATTRIB =
  "<a href='https://api.portal.hkmapservice.gov.hk/disclaimer' target='_blank' rel='noopener'>" +
  '© Map information from Lands Department 地圖資料由地政總署提供</a>'

const OSM_ATTRIB =
  '<a href="https://openfreemap.org" target="_blank" rel="noopener">OpenFreeMap</a> © ' +
  '<a href="https://www.openmaptiles.org/" target="_blank" rel="noopener">OpenMapTiles</a> © ' +
  '<a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors'

const xyz = (url, minzoom, maxzoom, attribution) => ({
  type: 'raster',
  tiles: [url],
  tileSize: 256,
  minzoom,
  maxzoom,
  attribution,
})

export const BASEMAPS = {
  street: {
    label_zh: '街道圖', label_en: 'Street',
    kind: 'style',
    attribution: OSM_ATTRIB,
  },

  landsd: {
    label_zh: '地政總署地圖', label_en: 'Lands Department',
    kind: 'raster',
    attribution: LANDSD_ATTRIB,
    // The Lands Department licence asks for the logo on the map face, not just
    // a text credit. Flagged here rather than inferred from the attribution
    // string, so the obligation survives someone rewording the credit.
    logo: 'landsd',
    // Two stacked rasters: an unlabelled topographic base, then a transparent
    // label sheet. Splitting them is what makes the language toggle possible —
    // switching to English swaps only the label sheet.
    layers: [
      {
        id: 'landsd-base',
        source: xyz(
          'https://mapapi.geodata.gov.hk/gs/api/v1.0.0/xyz/basemap/WGS84/{z}/{x}/{y}.png',
          10, 20, LANDSD_ATTRIB
        ),
      },
      {
        id: 'landsd-label',
        source: xyz(
          'https://mapapi.geodata.gov.hk/gs/api/v1.0.0/xyz/label/hk/{lang}/WGS84/{z}/{x}/{y}.png',
          9, 20, ''
        ),
        localised: true,   // {lang} is substituted with tc / en at runtime
      },
    ],
  },

  imagery: {
    label_zh: '航空照片', label_en: 'Aerial imagery',
    kind: 'raster',
    attribution: LANDSD_ATTRIB,
    // The Lands Department licence asks for the logo on the map face, not just
    // a text credit. Flagged here rather than inferred from the attribution
    // string, so the obligation survives someone rewording the credit.
    logo: 'landsd',
    layers: [
      {
        id: 'landsd-imagery',
        source: xyz(
          'https://mapapi.geodata.gov.hk/gs/api/v1.0.0/xyz/imagery/WGS84/{z}/{x}/{y}.png',
          10, 20, LANDSD_ATTRIB
        ),
      },
      {
        id: 'landsd-imagery-label',
        source: xyz(
          'https://mapapi.geodata.gov.hk/gs/api/v1.0.0/xyz/label/hk/{lang}/WGS84/{z}/{x}/{y}.png',
          9, 20, ''
        ),
        localised: true,
      },
    ],
  },
}

/** Lands Department uses tc/en/sc; we speak zh/en. */
export const landsdLang = (locale) => (locale === 'zh' ? 'tc' : 'en')

/** The Lands Department licence requires the logo on the map face, not just a
    text credit. Rendered as a corner <img> over the canvas. */
export const LANDSD_LOGO = 'https://api.hkmapservice.gov.hk/mapapi/landsdlogo.jpg'
